import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { UserService } from './user.service';
import { BehaviorSubject, Observable, catchError, distinctUntilChanged, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { TerminologyTestingStateService } from './terminology-testing-state.service';

@Injectable({
  providedIn: 'root',
})
export class LocaleService {
  private localeUpdateSequence = 0;
  private readonly localeSubject = new BehaviorSubject<string>(
    LocaleService.normalize(localStorage.getItem('ehr.locale') || 'en'),
  );
  readonly locale$ = this.localeSubject.asObservable().pipe(distinctUntilChanged());

  constructor(
    private userService: UserService,
    private http: HttpClient,
    private translate: TranslateService,
    private terminologyTestingState: TerminologyTestingStateService,
  ) {
    this.userService.localeSub
      .subscribe((locale) => {
        if (locale) this.setLocale(locale);
      });
  }

  static normalize(locale?: string | null): string {
    const value = (locale || 'en').trim().replace('_', '-');
    if (value.toLowerCase() === 'gr') return 'el';
    const [language, region] = value.split('-');
    if (!/^[a-z]{2,3}$/i.test(language)) return 'en';
    return region ? `${language.toLowerCase()}-${region.toUpperCase()}` : language.toLowerCase();
  }

  getCurrentLocale(): string {
    return this.localeSubject.value;
  }

  setLocale(locale: string): void {
    const normalized = LocaleService.normalize(locale);
    localStorage.setItem('ehr.locale', normalized);
    if (this.userService.localeSub.value !== normalized) this.userService.setLocale(normalized);
    if (this.localeSubject.value !== normalized) this.localeSubject.next(normalized);
    this.translate.use(normalized.split('-')[0]);
    if (!normalized.startsWith('el')) this.terminologyTestingState.clear();
  }

  updateLocale(locale: string): Observable<any> {
    const normalized = LocaleService.normalize(locale);
    const updateSequence = ++this.localeUpdateSequence;

    return this.http
      .put(
        `${environment.BACKEND_URL}UserManagement/ChangeLanguage/${normalized}`,
        {}
      )
      .pipe(
        tap(() => {
          if (updateSequence === this.localeUpdateSequence) this.setLocale(normalized);
        }),
        catchError((err) => {
          console.error(err.error);
          return throwError(() => err.error);
        })
      );
  }
}
